<script lang="ts">
  import type { JobFilters } from '$lib/careers/types';
  import { createEventDispatcher } from 'svelte';
  import Select from '$components/Select/Select.svelte';
  import { jobsFiltersStore, type JobFiltersState } from '$lib/stores/Careers/jobFiltersStore';
  import SearchFilter from './SearchFilter.svelte';
  import Toggle from '$components/Toggle/Toggle.svelte';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';

  interface Props {
    filterValues: JobFilters;
  }

  let { filterValues }: Props = $props();

  const dispatch = createEventDispatcher();

  function handleFilterChange<T>(updater: (state: JobFiltersState, value: T) => JobFiltersState) {
    return (event: CustomEvent<T>) => {
      jobsFiltersStore.update((state) => {
        return updater(state, event.detail);
      });

      dispatch('filters-change', $jobsFiltersStore);
    };
  }

  const handleSearchValueChange = handleFilterChange<string>((state, query) => ({
    ...state,
    query
  }));

  const handleLocationsChange = handleFilterChange<string[]>((state, locations) => ({
    ...state,
    locations
  }));

  const handleRemoteToggleChange = handleFilterChange<boolean>((state, isRemote) => ({
    ...state,
    isRemote
  }));

  const handleTeamsChange = handleFilterChange<string[]>((state, teams) => ({
    ...state,
    teams
  }));

  const handleEmployeeTypesChange = handleFilterChange<string[]>((state, employeeTypes) => ({
    ...state,
    employeeTypes
  }));

  const toOptions = (options: string[]) => {
    return options.map((option) => {
      return {
        value: option,
        label: option
      };
    });
  };

  const responsiveVars = new ResponsiveVars().addFluid('gap', toFluidVarInput(15, 9)).toCSS();
</script>

<div class={cx('filters-group', responsiveVars)}>
  <SearchFilter value={$jobsFiltersStore.query} on:change={handleSearchValueChange} />
  <Select
    selected={$jobsFiltersStore.teams}
    label="Team"
    options={toOptions(filterValues.teams)}
    on:change={handleTeamsChange}
  />

  <Select
    selected={$jobsFiltersStore.locations}
    label="Location"
    options={toOptions(filterValues.locations)}
    on:change={handleLocationsChange}
  >
    <Toggle
      label="Remote"
      value={$jobsFiltersStore.isRemote}
      on:change={handleRemoteToggleChange}
    />
  </Select>

  <Select
    selected={$jobsFiltersStore.employeeTypes}
    label="Type"
    options={toOptions(filterValues.employeeTypes)}
    on:change={handleEmployeeTypesChange}
  />
</div>

<style lang="scss">
  @import 'src/scss/media';

  .filters-group {
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    gap: var(--gap);

    @include at('lg') {
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    }
  }
</style>
